import { type ReactNode, useState } from 'react';
import type { Control, FieldErrors } from 'react-hook-form';
import {
  CaretDownIcon,
  CaretRightIcon,
  InfoIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { Button, Tooltip } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { HAZMAT_TEXT_1 } from '@utility/constants/strings';
import { rateText, type RateOption } from '../../lib/rates';
import type { ShippingFormValues } from '../../lib/shipping-form';
import { RateList } from './RateList';
import { FulfillButtons } from './ShipmentParts';
import {
  CheckboxField,
  NumberField,
  SelectField,
  TextInputField,
  toSelectOptions,
  type LegacyOption,
} from './fields';

export type ShipmentDdp = {
  duties?: number;
  taxes?: number;
  fees?: number;
  total?: number;
};

export type ShipmentSectionProps = {
  control: Control<ShippingFormValues>;
  errors: FieldErrors<ShippingFormValues>;
  /** A manual provider means we buy the label; a 3PL ships it for us. */
  isManual: boolean;
  /** Which of the two it is only settles once the warehouse is known. */
  isProviderPending?: boolean;
  providers: LegacyOption[];
  services: LegacyOption[];
  packages: LegacyOption[];
  contentTypes: LegacyOption[];
  metadataLoading?: boolean;
  /** The chosen package decides whether weight and size are asked for. */
  show: { dimensions: boolean; weight: boolean };
  /** Above the weight and size of a label: the cartons they edit, when the label has a choice of them. */
  cartons?: ReactNode;
  isUSPS?: boolean;
  isInternational?: boolean;
  isRFCRequired?: boolean;
  onCarrierChange: (value: string) => void;
  onServiceChange: (value: string) => void;
  rate?: number | null;
  rateCurrency?: string;
  rateError?: string;
  rateLoading?: boolean;
  rateNegotiate?: boolean;
  /** A caption under the rate; hidden while the rate loads or has failed. */
  rateNote?: string;
  /** Delivery time as the carrier words it, e.g. `3 days`. */
  time?: string | null;
  ddp?: ShipmentDdp | null;
  /** What the 3PL quoted for the whole fulfillment. */
  providerRate?: number | string | null;
  /** The services the chosen warehouse quoted, cheapest first. */
  rateOptions?: RateOption[];
  selectedRateKey?: string;
  cheapestRateKey?: string;
  ratesLoading?: boolean;
  onSelectRate?: (option: RateOption | null) => void;
  orgName?: string;
  orgLogo?: string;
  allowAutomaticRate?: boolean;
  isSubmitDisabled?: boolean;
  isFulfillmentDisabled?: boolean;
  isBusy?: boolean;
  batchOrder?: boolean;
  allowTestMode?: boolean;
  invalidAddress?: boolean;
  onValidateAddress: () => void;
  onFulfill: (testMode?: boolean) => void;
  /** Absent unless a fulfill queue has somewhere left to go. */
  onFulfillAndNext?: () => void;
  onCreateTestLabel: () => void;
  orderError?: string;
  /** EN-4544: the main button sends the shipment to its provider (V2): a rate the provider did not quote is left out. */
  sendsToProvider?: boolean;
  /** EN-4544: the fulfill buttons stand under every shipment instead, as one press fulfills them all. */
  hidesFulfill?: boolean;
};

function MoneyRow({
  label,
  value,
  currency,
}: {
  label: string;
  value?: number;
  currency?: string;
}) {
  return (
    <span className="sk:flex sk:items-center sk:justify-between sk:text-dense">
      <span className="sk:text-fg-tertiary">{label}</span>
      <span className="sk:text-fg">
        {CommonUtility.currencyFormat(value, currency)}
      </span>
    </span>
  );
}

/**
 * How the order ships and what it costs: the services on offer for a 3PL, the
 * carrier form for a manual label, and the action that pays for it.
 */
export function ShipmentSection({
  control,
  errors,
  isManual,
  isProviderPending,
  providers,
  services,
  packages,
  contentTypes,
  metadataLoading,
  show,
  cartons,
  isUSPS,
  isInternational,
  isRFCRequired,
  onCarrierChange,
  onServiceChange,
  rate,
  rateCurrency,
  rateError,
  rateLoading,
  rateNegotiate,
  rateNote,
  time,
  ddp,
  providerRate,
  rateOptions = [],
  selectedRateKey = 'any',
  cheapestRateKey,
  ratesLoading,
  onSelectRate,
  orgName,
  orgLogo,
  allowAutomaticRate = true,
  isSubmitDisabled,
  isFulfillmentDisabled,
  isBusy,
  batchOrder,
  allowTestMode,
  invalidAddress,
  onValidateAddress,
  onFulfill,
  onFulfillAndNext,
  onCreateTestLabel,
  orderError,
  sendsToProvider,
  hidesFulfill,
}: ShipmentSectionProps) {
  const [showMore, setShowMore] = useState(false);
  const fulfillmentDisabled = invalidAddress || isBusy || isFulfillmentDisabled;

  if (isProviderPending) {
    return (
      <div className="sk:flex sk:flex-col sk:gap-3 sk:border-t sk:border-rule sk:pt-4">
        {[0, 1, 2].map(row => (
          <div
            key={row}
            className="sk:h-9 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary"
          />
        ))}
        <div className="sk:h-11 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
      </div>
    );
  }

  const total = ddp ? (ddp.total || 0) + (rate || 0) : rate;
  /* A picked service is priced by its own row; the warehouse quote covers its own choice. */
  const selectedRate =
    rateOptions.find(option => option.key === selectedRateKey)?.rate ??
    providerRate;
  /* A provider that quoted nothing has no rate to show: Send does not need one. */
  const hidesRate = !!sendsToProvider && !rateLoading && selectedRate == null;

  /* EN-4544: with no quote and its buttons under every shipment, a provider send has nothing left to show here. */
  if (
    !isManual &&
    hidesFulfill &&
    hidesRate &&
    !ratesLoading &&
    rateOptions.length === 0 &&
    !ddp &&
    !invalidAddress &&
    !allowTestMode &&
    !orderError
  ) {
    return null;
  }
  const priceLabel =
    !isManual || rateLoading || rateError || !CommonUtility.isNotEmpty(total)
      ? ''
      : ` — ${CommonUtility.currencyFormat(total, rateCurrency)}`;

  const contentTypeField = (
    <SelectField
      control={control}
      name="customsContentType"
      label="Content type"
      placeholder="Select content type"
      options={toSelectOptions(contentTypes)}
      isDisabled={contentTypes.length === 0}
      error={errors.customsContentType?.message}
      isRequired={isInternational}
    />
  );

  return (
    <div className="sk:flex sk:flex-col sk:gap-4 sk:border-t sk:border-rule sk:pt-4">
      {isManual ? (
        <>
          <div className="sk:grid sk:gap-3 sk:grid-cols-[repeat(auto-fit,minmax(160px,1fr))]">
            <SelectField
              control={control}
              name="provider"
              label="Carrier"
              placeholder="Select carrier"
              options={toSelectOptions(providers)}
              isDisabled={metadataLoading}
              error={errors.provider?.message}
              onSelect={onCarrierChange}
            />
            <SelectField
              control={control}
              name="service"
              label="Service"
              placeholder="Select service"
              options={toSelectOptions(services)}
              isDisabled={metadataLoading || services.length === 0}
              error={errors.service?.message}
              onSelect={onServiceChange}
            />
            <SelectField
              control={control}
              name="package"
              label="Package"
              placeholder="Select package"
              options={toSelectOptions(packages)}
              isDisabled={metadataLoading || packages.length === 0}
              error={errors.package?.message}
            />
          </div>

          {cartons}

          {(show.weight || show.dimensions) && (
            <div className="sk:grid sk:gap-3 sk:grid-cols-[repeat(auto-fit,minmax(90px,1fr))]">
              {show.weight && (
                <>
                  <NumberField
                    control={control}
                    name="pounds"
                    label="Package weight (lb)"
                    placeholder="0"
                    error={errors.pounds?.message}
                  />
                  <NumberField
                    control={control}
                    name="ounces"
                    label="Package weight (oz)"
                    placeholder="0"
                    error={errors.ounces?.message}
                  />
                </>
              )}
              {show.dimensions && (
                <>
                  <NumberField
                    control={control}
                    name="length"
                    label="Length (in)"
                    placeholder="0"
                    error={errors.length?.message}
                  />
                  <NumberField
                    control={control}
                    name="width"
                    label="Width (in)"
                    placeholder="0"
                    error={errors.width?.message}
                  />
                  <NumberField
                    control={control}
                    name="height"
                    label="Height (in)"
                    placeholder="0"
                    error={errors.height?.message}
                  />
                </>
              )}
            </div>
          )}

          <div className="sk:flex sk:flex-col sk:gap-3">
            {isInternational && contentTypeField}

            <Button
              variant="ghost"
              size="sm"
              className="sk:self-start sk:px-0"
              onPress={() => setShowMore(value => !value)}
            >
              {showMore ? (
                <CaretDownIcon size={10} weight="bold" />
              ) : (
                <CaretRightIcon size={10} weight="bold" />
              )}
              More options
            </Button>

            {showMore && (
              <div className="sk:flex sk:flex-col sk:gap-3">
                {!isInternational && contentTypeField}
                {isRFCRequired && (
                  <TextInputField
                    control={control}
                    name="options.tax_code"
                    label="RFC code"
                    placeholder="Enter Mexican Tax ID"
                  />
                )}
                <TextInputField
                  control={control}
                  name="contentComments"
                  label="Comments"
                />
                {isUSPS && (
                  <CheckboxField control={control} name="options.hazmat">
                    <span className="sk:flex sk:items-center sk:gap-1">
                      Contains hazmat
                      <Tooltip content={HAZMAT_TEXT_1}>
                        <InfoIcon size={12} className="sk:text-fg-tertiary" />
                      </Tooltip>
                    </span>
                  </CheckboxField>
                )}
                <CheckboxField control={control} name="packing_list">
                  Include packing list
                </CheckboxField>
                <CheckboxField control={control} name="saveDefault">
                  Save as shipping defaults for this product
                </CheckboxField>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {!allowAutomaticRate ? (
            <span className="sk:flex sk:items-center sk:gap-2 sk:text-dense sk:text-warning">
              <WarningIcon size={14} />
              Select a specific carrier service to replace the refunded label.
            </span>
          ) : null}
          {ratesLoading || rateOptions.length > 0 ? (
            <RateList
              options={rateOptions}
              selectedKey={selectedRateKey}
              cheapestKey={cheapestRateKey}
              onSelect={option => onSelectRate?.(option)}
              currency={rateCurrency}
              isLoading={ratesLoading}
              orgName={orgName}
              orgLogo={orgLogo}
              allowAutomatic={allowAutomaticRate}
            />
          ) : null}
          {isRFCRequired ? (
            <TextInputField
              control={control}
              name="options.tax_code"
              label="RFC code"
              placeholder="Enter Mexican Tax ID"
            />
          ) : null}
        </>
      )}

      <div className="sk:flex sk:flex-col sk:gap-2">
        {ddp ? (
          <>
            <MoneyRow
              label="Duties (DDP)"
              value={ddp.duties}
              currency={rateCurrency}
            />
            <MoneyRow label="Taxes" value={ddp.taxes} currency={rateCurrency} />
            <MoneyRow label="Fees" value={ddp.fees} currency={rateCurrency} />
          </>
        ) : null}

        {hidesRate ? null : (
          <span className="sk:flex sk:items-end sk:justify-between sk:gap-2">
            <span className="sk:flex sk:flex-col">
              <span className="sk:text-xs sk:text-fg-tertiary">
                {isManual ? 'Label total' : 'Fulfillment rate'}
              </span>
              <span className="sk:text-lg sk:font-semibold sk:text-fg">
                {rateLoading
                  ? 'Loading'
                  : isManual
                    ? rateError
                      ? '—'
                      : rateText(total, rateCurrency)
                    : rateText(selectedRate, rateCurrency)}
              </span>
              {rateNegotiate ? (
                <span className="sk:text-xs sk:text-fg-tertiary">
                  Negotiated rate
                </span>
              ) : null}
              {rateNote && !rateLoading && !rateError ? (
                <span className="sk:text-xs sk:text-fg-tertiary">
                  {rateNote}
                </span>
              ) : null}
            </span>
            {isManual ? (
              <span className="sk:flex sk:flex-col sk:items-end">
                <span className="sk:text-xs sk:text-fg-tertiary">Delivery</span>
                <span className="sk:text-dense sk:font-medium sk:text-fg">
                  {rateLoading ? 'Loading' : time || '—'}
                </span>
              </span>
            ) : null}
          </span>
        )}

        {invalidAddress ? (
          <Button
            variant="pill"
            size="lg"
            className="sk:w-full sk:text-danger"
            onPress={onValidateAddress}
          >
            <WarningIcon size={14} weight="bold" />
            Validate address
          </Button>
        ) : null}

        {isManual ? (
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="sk:w-full"
            isDisabled={isSubmitDisabled}
          >
            {batchOrder ? 'Save and continue' : 'Create label'}
            {priceLabel}
          </Button>
        ) : hidesFulfill ? null : (
          <FulfillButtons
            isDisabled={fulfillmentDisabled}
            onFulfill={() => onFulfill()}
            onFulfillAndNext={onFulfillAndNext}
          />
        )}

        {allowTestMode ? (
          <Button
            type="button"
            variant="pill"
            size="lg"
            className="sk:w-full"
            isDisabled={isManual ? isSubmitDisabled : fulfillmentDisabled}
            onPress={() => (isManual ? onCreateTestLabel() : onFulfill(true))}
          >
            {isManual
              ? batchOrder
                ? 'Save and continue (test)'
                : 'Create test label'
              : 'Test fulfillment'}
          </Button>
        ) : null}

        {orderError ? (
          <span className="sk:text-dense sk:text-danger">{orderError}</span>
        ) : null}
      </div>
    </div>
  );
}
